import {
  HEADER_SECONDARY_BUTTON,
  HEADER_SECONDARY_ICON,
} from "../../components/layout/headerActionStyles";
import { AdsManagerSkeleton, Button, PageLayout } from "@/components";
import Swal from "sweetalert2";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Eraser,
  Zap,
  X,
  Save,
} from "lucide-react";
import React, { Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../contexts/LanguageContext";
import { AdsProvider, useAds } from "./hooks/AdsContext";

// Modular Steps
import Step1_GeneralConfig from "./components/Step1_GeneralConfig";
import Step2_CreativeEditor from "./components/Step2_CreativeEditor";
import Step3_Targeting from "./components/Step3_Targeting";
import Step4_LeadForm from "./components/Step4_LeadForm";
import Step4_Review from "./components/Step4_Review";

const AdsManagerContent: React.FC = () => {
  const navigate = useNavigate();
  const {
    currentStep,
    setCurrentStep,
    steps,
    nextStep,
    prevStep,
    isLastStep,
    canGoNext,
    charLimitIssues,
    missingFieldIssues,
    loading,
    handleLaunchCampaign,
    handleSaveDraft,
    setName,
    setDailyBudget,
    setLinkUrl,
    setMessage,
    destinationType,
    draftId,
    platform,
    linkedinObjective,
  } = useAds();
  const { t } = useTranslation();

  const handleExit = async () => {
    const { isConfirmed } = await Swal.fire({
      title: t("Tem certeza que deseja sair?"),
      text: t(
        "Seu progresso atual não será salvo caso você não tenha clicado em 'Salvar Rascunho'.",
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("Sim, sair"),
      cancelButtonText: t("Voltar"),
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#7C3AED",
    });

    if (isConfirmed) {
      navigate(-1); // Volta para a página anterior
    }
  };

  // `loading` no AdsContext é compartilhado entre o carregamento inicial do
  // rascunho e as ações de salvar/lançar — usamos esse flag só para decidir
  // se o skeleton (formatado como Step 1) já foi mostrado uma vez, evitando
  // que a tela inteira seja substituída pelo skeleton no meio de um salvamento
  // ou lançamento (quando o usuário já está em outra etapa).
  const hasLoadedOnceRef = React.useRef(false);
  if (!loading) hasLoadedOnceRef.current = true;
  const showInitialSkeleton = loading && !hasLoadedOnceRef.current;

  // Auto-scroll para o topo sempre que mudar de etapa
  React.useEffect(() => {
    const mainContent = document.getElementById("main-content");
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStep]);

  const renderStep = () => {
    // Mapeamento dinâmico: Passo 1, 2 e 3 são fixos.
    // Se houver formulário: Passo 4 = Form, Passo 5 = Revisão.
    // Se não houver: Passo 4 = Revisão.
    switch (currentStep) {
      case 1:
        return <Step1_GeneralConfig />;
      case 2:
        return <Step2_CreativeEditor />;
      case 3:
        return <Step3_Targeting />;
      case 4:
        return destinationType === "form" ||
          (platform === "linkedin" && linkedinObjective === "leads") ? (
          <Step4_LeadForm />
        ) : (
          <Step4_Review />
        );
      case 5:
        return <Step4_Review />;
      default:
        return <Step1_GeneralConfig />;
    }
  };

  return (
    <PageLayout
      title={t("Lançamento de Campanha Unificado")}
      subtitle={
        <div className="flex flex-col gap-1">
          <span>
            {t(
              "Configure sua estratégia de anúncios para Meta, Google e LinkedIn em um único fluxo assistido por IA.",
            )}
          </span>
          {draftId && (
            <span className="text-xs font-mono text-purple-400 bg-purple-50 self-start px-2 py-0.5 rounded-full border border-purple-100">
              {t("Rascunho ID: #{draftId}", { draftId })}
            </span>
          )}
        </div>
      }
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-4 py-2"
      loading={showInitialSkeleton}
      skeleton={<AdsManagerSkeleton />}
      actions={
        <div className="flex items-center gap-2">
          <Button
            onClick={async () => {
              const { isConfirmed } = await Swal.fire({
                title: t("Limpar campos?"),
                text: t(
                  "Isso resetará todos os dados preenchidos até agora neste fluxo.",
                ),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: t("Sim, limpar tudo"),
                cancelButtonText: t("Manter dados"),
                confirmButtonColor: "#7C3AED",
              });

              if (isConfirmed) {
                setName("");
                setDailyBudget("");
                setLinkUrl("");
                setMessage("");
                setCurrentStep(1);
              }
            }}
            variant="outline"
            size="sm"
            className={HEADER_SECONDARY_BUTTON}
            icon={<Eraser className={HEADER_SECONDARY_ICON} />}
          >
            <span className="hidden sm:inline text-xs">{t("Limpar Tudo")}</span>
            <span className="sm:hidden text-xs">{t("Limpar")}</span>
          </Button>
        </div>
      }
    >
      <div className="w-full flex flex-col min-h-[calc(100vh-150px)]">
        {/* Compact Stepper */}
        <div className="bg-white border border-purple-200/70 rounded-2xl p-2.5 px-3 sm:px-4 shadow-xs mb-3 overflow-x-auto">
          <nav className="flex items-center justify-between min-w-max sm:min-w-0 relative gap-2 sm:gap-0">
            {/* Connecting Line */}
            <div className="absolute top-1/2 left-0 w-full h-0.5 bg-purple-50 -translate-y-1/2 z-0" />

            {steps.map((step) => {
              const active = currentStep === step.id;
              const completed = currentStep > step.id;
              return (
                <button
                  key={step.id}
                  onClick={() => completed && setCurrentStep(step.id)}
                  className={`relative z-10 flex items-center gap-2 sm:gap-2.5 px-2.5 sm:px-3 py-1 rounded-xl transition-all duration-200 shrink-0 ${
                    active ? "bg-purple-50/80 shadow-xs" : ""
                  } ${completed || active ? "cursor-pointer" : "cursor-default opacity-50"}`}
                >
                  <div
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center transition-all duration-300 border-2 shrink-0 ${
                      active
                        ? "bg-purple-600 text-white border-purple-200 shadow-md shadow-purple-300/40"
                        : completed
                          ? "bg-green-500 text-white border-green-100"
                          : "bg-white text-gray-300 border-purple-50"
                    }`}
                  >
                    <step.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </div>
                  <div className="flex flex-col items-start text-left">
                    <span
                      className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-wider leading-none ${active ? "text-purple-700" : completed ? "text-green-600" : "text-gray-400"}`}
                    >
                      {t(step.label)}
                    </span>
                    <span className="hidden lg:block text-[9px] text-gray-400 font-medium max-w-[120px] leading-tight mt-0.5 line-clamp-1">
                      {t(step.description)}
                    </span>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content Area with Bottom Breathing Room */}
        <div className="flex-1 mb-4 pb-4">
          <Suspense
            fallback={
              <div className="w-full h-48 flex items-center justify-center">
                <div className="w-8 h-8 border-[3px] border-purple-200 border-t-purple-600 rounded-full animate-spin" />
              </div>
            }
          >
            {renderStep()}
          </Suspense>
        </div>

        {/* Compact Sticky Action Bar */}
        <div className="sticky bottom-4 py-2.5 px-3 sm:py-3 sm:px-5 bg-white/95 backdrop-blur-xl border border-purple-200/70 rounded-2xl z-40 shadow-lg shadow-purple-900/10 mt-auto w-full relative overflow-hidden">
          {/* Micro-barra de progresso */}
          <div
            className="absolute top-0 left-0 h-[2px] bg-linear-to-r from-purple-400 to-indigo-500 transition-all duration-300"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />

          {/* Aviso do que falta para avançar — campos obrigatórios vazios
              e/ou campos com excesso de caracteres. Explica visualmente
              por que "Próximo Passo"/"Lançar Campanha" está desabilitado,
              em vez de só desabilitar o botão silenciosamente. */}
          {(missingFieldIssues.length > 0 || charLimitIssues.length > 0) && (
            <div className="flex items-start gap-1.5 mb-2 px-2.5 py-1.5 bg-red-50 border border-red-200 rounded-lg text-[10px] font-bold text-red-600 animate-in fade-in duration-200">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span>
                {t("Para avançar:")}{" "}
                {[
                  ...missingFieldIssues,
                  ...charLimitIssues.map(
                    (i) => `${i.label} (${i.current}/${i.max})`,
                  ),
                ].join(" · ")}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            {currentStep === 1 ? (
              <Button
                onClick={handleExit}
                variant="outline"
                size="sm"
                className="px-3 sm:px-5 rounded-xl h-9 border-red-200 text-red-600 hover:bg-red-50! hover:text-red-700! text-xs font-semibold transition-colors"
                icon={<X className="w-3.5 h-3.5" />}
              >
                {t("Sair")}
              </Button>
            ) : (
              <Button
                onClick={prevStep}
                variant="outline"
                size="sm"
                className="px-3 sm:px-5 rounded-xl h-9 border-purple-200 text-purple-600 text-xs font-semibold"
                icon={<ChevronLeft className="w-3.5 h-3.5" />}
              >
                {t("Voltar")}
              </Button>
            )}

            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                onClick={handleSaveDraft}
                variant="ghost"
                size="sm"
                className="bg-white border border-gray-200 text-gray-500 shadow-[0_2px_4px_rgba(0,0,0,0.02)] hover:shadow-md hover:border-purple-200 hover:bg-purple-50 hover:text-purple-700 rounded-xl px-2.5 sm:px-4 h-9 text-xs font-semibold transition-all"
                isLoading={loading}
                icon={
                  <Save className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />
                }
              >
                <span className="hidden sm:inline">{t("Salvar Rascunho")}</span>
                <span className="sm:hidden">{t("Salvar")}</span>
              </Button>
              {!isLastStep ? (
                <Button
                  onClick={nextStep}
                  disabled={!canGoNext()}
                  variant="primary"
                  size="sm"
                  className="px-4 sm:px-6 rounded-xl h-9 bg-purple-600 hover:bg-purple-700 shadow-md shadow-purple-200 text-xs font-bold whitespace-nowrap"
                  iconPosition="right"
                  icon={<ChevronRight className="w-3.5 h-3.5" />}
                >
                  {t("Próximo Passo")}
                </Button>
              ) : (
                <Button
                  onClick={handleLaunchCampaign}
                  disabled={loading || !canGoNext()}
                  variant="primary"
                  size="sm"
                  className="px-4 sm:px-6 rounded-xl h-9 bg-linear-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-xs hover:shadow-md shadow-purple-500/20 active:scale-[0.98] text-xs font-bold whitespace-nowrap transition-all"
                  icon={<Zap className="w-3.5 h-3.5" />}
                >
                  {loading ? t("Lançando...") : t("Lançar Campanha")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

const AdsManager: React.FC = () => (
  <AdsProvider>
    <AdsManagerContent />
  </AdsProvider>
);

export default AdsManager;
